import { useRef } from 'react';
import { User, Loader2, Edit3, Target, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ProfileHeaderCardProps {
  userName: string;
  email: string;
  avatar: string;
  loadingAvatar: boolean;
  mainGoal: string | null;
  protocolStatus?: string;
  onAvatarChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onEditProfile: () => void;
}

export const ProfileHeaderCard = ({
  userName,
  email,
  avatar,
  loadingAvatar,
  mainGoal,
  protocolStatus = 'Día 1 de 21',
  onAvatarChange,
  onEditProfile,
}: ProfileHeaderCardProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Map backend goals to human readable text if needed
  const displayGoal = mainGoal === 'reduzir_inchaco' ? 'Reducir hinchazón' :
                     mainGoal === 'mais_energia' ? 'Más energía' :
                     mainGoal === 'melhorar_digestao' ? 'Mejorar digestión' :
                     mainGoal === 'emagrecimento' ? 'Pérdida de peso' :
                     mainGoal || 'Bienestar general';

  return (
    <div className="bg-card rounded-2xl p-5 shadow-card">
      <div className="flex items-start gap-4">
        {/* Avatar Section */}
        <div 
          className="w-20 h-20 gradient-primary rounded-2xl flex items-center justify-center relative cursor-pointer overflow-hidden group flex-shrink-0"
          onClick={() => !loadingAvatar && fileInputRef.current?.click()}
        >
          {loadingAvatar ? (
            <Loader2 className="h-6 w-6 text-white animate-spin" />
          ) : avatar ? (
            <img src={avatar} alt="Profile" className="w-full h-full object-cover" />
          ) : (
            <User className="h-10 w-10 text-primary-foreground" />
          )}
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <span className="text-white text-[10px] font-medium">Cambiar</span>
          </div>
          <input 
            type="file" 
            ref={fileInputRef} 
            onChange={onAvatarChange} 
            accept="image/*" 
            className="hidden" 
          />
        </div>

        {/* User Info */}
        <div className="flex-1 min-w-0 pt-1">
          <h2 className="font-heading font-bold text-xl truncate text-foreground">{userName}</h2>
          <p className="text-xs text-muted-foreground truncate mb-3" title="El correo no se puede cambiar">
            {email}
          </p>
          
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5 text-xs text-secondary-foreground">
              <Target className="h-3.5 w-3.5 text-primary" />
              <span className="font-medium truncate">Objetivo: {displayGoal}</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-secondary-foreground">
              <Calendar className="h-3.5 w-3.5 text-primary" />
              <span className="font-medium truncate">Protocolo: {protocolStatus}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Action Button */}
      <div className="mt-5">
        <Button 
          variant="outline" 
          className="w-full h-9 text-xs rounded-xl border-primary/20 text-primary hover:bg-primary/5"
          onClick={onEditProfile}
        >
          <Edit3 className="h-3.5 w-3.5 mr-2" /> Editar perfil
        </Button>
      </div>
    </div>
  );
};
